// frontend/src/pages/portfolio/sections/ProductShowcase.tsx
import React from "react";
import { BarChart3, CheckCircle2, Clock, Layers, Users } from "lucide-react";
import DashboardMockup from "../components/DashboardMockup";
import SectionEyebrow from "../components/SectionEyebrow";
import { container, sectionHeading, sectionLede, sectionReveal } from "../components/styles";

const adminPoints = [
  "Live activity, hours, and rates at a glance",
  "Drill into any day or employee",
  "Export-ready reports for payroll",
];

const employeePoints = [
  "Clock in and out without friction",
  "Daily summary saved with each session",
  "Optional billing entries for client work",
];

const ProductShowcase: React.FC = () => {
  return (
    <section
      id="product"
      className="border-t border-primary-200/30 bg-primary-50/30 py-16 sm:py-20"
      aria-labelledby="product-heading"
    >
      <div className={container}>
        <div className={`max-w-2xl ${sectionReveal}`}>
          <SectionEyebrow icon={Layers}>Product</SectionEyebrow>
          <h2 id="product-heading" className={`mt-4 ${sectionHeading}`}>
            See what your team gets, on day one.
          </h2>
          <p className={sectionLede}>
            Two surfaces, one source of truth. Admins get the bird&apos;s-eye
            view; employees get a calm clock-in flow.
          </p>
        </div>

        {/* Admin */}
        <div className={`mt-14 grid gap-10 lg:grid-cols-[1.1fr_0.9fr] lg:items-center lg:gap-16 ${sectionReveal}`}>
          <div>
            <span className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1 text-xs font-semibold text-primary-800 ring-1 ring-primary-200">
              <Users className="h-3.5 w-3.5" aria-hidden />
              For admins
            </span>
            <h3 className="mt-4 font-display text-2xl font-semibold text-primary-950 sm:text-3xl">
              A dashboard that earns its name.
            </h3>
            <ul className="mt-6 space-y-3">
              {adminPoints.map((p) => (
                <li key={p} className="flex items-start gap-3">
                  <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-success-600" aria-hidden />
                  <span className="text-base leading-relaxed text-gray-700">{p}</span>
                </li>
              ))}
            </ul>
          </div>
          <div>
            <DashboardMockup />
          </div>
        </div>

        {/* Employee */}
        <div className={`mt-20 grid gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:items-center lg:gap-16 ${sectionReveal}`}>
          <div className="order-2 lg:order-1">
            {/* Employee mock */}
            <div className="relative" aria-hidden>
              <div className="absolute -inset-4 rounded-[1.75rem] bg-gradient-to-br from-primary-200/40 via-transparent to-primary-400/15 blur-2xl motion-reduce:hidden" />
              <div className="relative rounded-2xl border border-gray-200 bg-white p-6 shadow-elevated ring-1 ring-primary-900/[0.04] sm:p-8">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-[11px] font-semibold uppercase tracking-wider text-primary-700/70">
                      Today
                    </div>
                    <div className="font-display text-lg font-semibold text-primary-950">
                      Good morning, Alex
                    </div>
                  </div>
                  <span className="rounded-full bg-success-50 px-2.5 py-1 text-xs font-semibold text-success-700 ring-1 ring-success-200">
                    Ready
                  </span>
                </div>
                <button
                  type="button"
                  tabIndex={-1}
                  className="mt-6 flex w-full items-center justify-center gap-2 rounded-xl bg-primary-900 px-5 py-4 font-display text-base font-semibold text-white shadow-elevated"
                >
                  <Clock className="h-5 w-5" aria-hidden />
                  Clock in
                </button>
                <div className="mt-5 grid grid-cols-3 gap-2 text-center">
                  {[
                    { label: "Net", value: "0h" },
                    { label: "Idle", value: "0m" },
                    { label: "Sessions", value: "0" },
                  ].map((s) => (
                    <div key={s.label} className="rounded-lg bg-slate-50 py-2 ring-1 ring-gray-100">
                      <div className="font-display text-sm font-bold tabular-nums text-primary-950">
                        {s.value}
                      </div>
                      <div className="text-[10px] font-medium uppercase tracking-wider text-primary-700/60">
                        {s.label}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
          <div className="order-1 lg:order-2">
            <span className="inline-flex items-center gap-2 rounded-full bg-white px-3 py-1 text-xs font-semibold text-primary-800 ring-1 ring-primary-200">
              <Clock className="h-3.5 w-3.5" aria-hidden />
              For employees
            </span>
            <h3 className="mt-4 font-display text-2xl font-semibold text-primary-950 sm:text-3xl">
              Clock in. Get on with the day.
            </h3>
            <ul className="mt-6 space-y-3">
              {employeePoints.map((p) => (
                <li key={p} className="flex items-start gap-3">
                  <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-success-600" aria-hidden />
                  <span className="text-base leading-relaxed text-gray-700">{p}</span>
                </li>
              ))}
            </ul>
            <p className="mt-6 inline-flex items-center gap-2 text-sm text-primary-700">
              <BarChart3 className="h-4 w-4" aria-hidden />
              Activity insights stay private to admins, by role.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProductShowcase;
